import { useForm } from 'react-hook-form'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { useUserStore } from '@/store/UserStore'
import Gravatar from 'react-gravatar'
import { useUpdateUserProfile } from '@/services/profile'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { useActiveOrgProfile } from '@/hooks/useActiveOrgProfile'
import { capitalize } from '@/helpers/string'
import { Camera } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  FileUploadSchema,
  ProfileFormValues,
  ValidationMessages,
} from './types'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Separator } from '@/components/ui/separator'
import { UpdateOrgUserProfileV2Args } from '@/services/inference.gen'
import { toast } from 'react-hot-toast'

const ProfileFormSchema = z.object({
  firstName: z
    .string()
    .min(1, ValidationMessages.required('First name'))
    .max(50, ValidationMessages.maxLength('First name', 50)),
  lastName: z
    .string()
    .min(1, ValidationMessages.required('Last name'))
    .max(50, ValidationMessages.maxLength('Last name', 50)),
  avatar: FileUploadSchema,
})

export const MyProfileForm = () => {
  const { user } = useUserStore()
  const { data } = useActiveOrgProfile()
  const { mutateAsync, isPending } = useUpdateUserProfile()

  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(ProfileFormSchema),
    defaultValues: {
      firstName: data?.profile?.first_name || '',
      lastName: data?.profile?.last_name || '',
    },
    mode: 'onChange',
  })

  const avatarPreviewUrl = (() => {
    const avatarFile = form.watch('avatar')
    if (avatarFile) {
      return URL.createObjectURL(avatarFile)
    }
    return data?.profile?.avatar_preview_url
  })()

  const onSubmit = async (formData: ProfileFormValues) => {
    const reader = new FileReader()

    try {
      reader.onload = async () => {
        const base64File = (reader.result as string)?.split(',')[1] // remove "data:<type>;base64," prefix

        const payload: UpdateOrgUserProfileV2Args = {
          first_name: capitalize(formData.firstName),
          last_name: capitalize(formData.lastName),
        }

        if (formData.avatar) {
          payload.file = {
            file_content: base64File,
            file_name: formData.avatar.name || '',
            file_type: formData.avatar.type || '',
          }
        }

        await mutateAsync(payload)
      }

      if (formData.avatar) {
        reader.readAsDataURL(formData.avatar)
      } else {
        const payload: UpdateOrgUserProfileV2Args = {
          first_name: capitalize(formData.firstName),
          last_name: capitalize(formData.lastName),
        }
        await mutateAsync(payload)
      }
    } catch (error) {
      console.error(error)
      toast.error(JSON.stringify(error))
    }
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex flex-col gap-6 w-full max-w-3xl"
      >
        <Card className="border-primary/20 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl font-semibold text-primary">
              Personal Information
            </CardTitle>
          </CardHeader>

          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="flex flex-col items-center justify-start space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground self-center">
                Profile Picture
              </h3>

              <FormField
                control={form.control}
                name="avatar"
                render={({ field }) => (
                  <FormItem className="w-full flex flex-col items-center">
                    <FormControl>
                      <div className="relative group">
                        <div className="w-40 h-40 rounded-full overflow-hidden border-2 border-primary/30 flex items-center justify-center bg-muted/30">
                          {avatarPreviewUrl ? (
                            <img
                              src={avatarPreviewUrl}
                              alt="Profile"
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <Avatar className="w-full h-full">
                              <AvatarFallback className="bg-muted w-full h-full">
                                <Gravatar
                                  email={user?.email}
                                  className="w-full h-full"
                                />
                              </AvatarFallback>
                            </Avatar>
                          )}
                        </div>
                        <div className="absolute inset-0 flex items-center justify-center bg-foreground/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                          <Camera className="h-8 w-8 text-background" />
                        </div>
                        <input
                          id="avatar"
                          type="file"
                          accept="image/*"
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer rounded-full"
                          onChange={(e) => {
                            const file = e.target.files?.[0]
                            if (!file) return

                            if (!file.type.startsWith('image/')) {
                              form.setError('avatar', {
                                message: ValidationMessages.fileType('image'),
                              })
                              return
                            }

                            if (file.size > 500 * 1024) {
                              form.setError('avatar', {
                                message: ValidationMessages.fileSize('500KB'),
                              })
                              return
                            }

                            field.onChange(file)
                          }}
                        />
                      </div>
                    </FormControl>
                    <p className="text-xs text-center text-muted-foreground max-w-[200px] mt-2">
                      Click to upload a new photo (max 500KB)
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="md:col-span-2 space-y-6">
              <div className="space-y-2">
                <h3 className="text-sm font-medium text-muted-foreground">
                  Account
                </h3>
                <Separator className="mb-4" />
                <div className="space-y-2">
                  <FormLabel>Email Address</FormLabel>
                  <Input
                    disabled // KEEP IT DISABLED
                    type="email"
                    value={user?.email}
                  />
                  <p className="text-xs text-muted-foreground">
                    Your email address cannot be changed
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <h3 className="text-sm font-medium text-muted-foreground">
                  User Details
                </h3>
                <Separator className="mb-4" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>First Name</FormLabel>
                        <FormControl>
                          <Input
                            type="text"
                            className={cn('w-full', 'focus:ring-primary/20')}
                            placeholder="Enter your first name"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="lastName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Last Name</FormLabel>
                        <FormControl>
                          <Input
                            type="text"
                            className={cn('w-full', 'focus:ring-primary/20')}
                            placeholder="Enter your last name"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end mt-4">
          <Button
            type="submit"
            disabled={
              isPending || Object.keys(form.formState.errors).length > 0
            }
            className="px-6"
          >
            {isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </form>
    </Form>
  )
}
